import React, { useEffect, useState } from "react";
import { toastify } from '../../../utils/toast';
import { getItem } from '../../../utils/localStorage';
import { makeRequest2 } from '../../../utils/makeRequest';
import { getContracts, getWHTrecordsURL } from '../../../utils/urls';

const WHTReport = () => {
    const [loading, setLoading] = useState(false);
    const [whtContracts, setWHTContracts] = useState([]);
    const [whtRecords, setWHTRecords] = useState([]);
    const [whtStats, setWHTStats] = useState({
        totalRecords: 0,
        totalWithheld: 0
    });
    const [showWHTModal, setShowWHTModal] = useState(false);

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-KE', {
            style: 'currency',
            currency: 'KES'
        }).format(amount);
    };

    const fetchWHTContracts = async () => {
        try {
            setLoading(true);
            const facilityId = await getItem("selectedFacilityId");
            const response = await makeRequest2(
                `${getContracts}/${facilityId}?taxType=withholding`,
                "GET"
            );
            if (response?.success) {
                setWHTContracts(response.data.contracts || []);
            } else {
                setWHTContracts([]);
            }
        } catch (error) {
            toastify("Error fetching WHT contracts", "error");
            setWHTContracts([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchWHTrecords = async () => {
        try {
            const facilityId = await getItem("selectedFacilityId");
            const response = await makeRequest2(
                `${getWHTrecordsURL}/${facilityId}`,
                "GET"
            );
            if (response?.success) {
                setWHTRecords(response.data.records || []);
                setWHTStats(
                    response.data.statistics || {
                        totalRecords: 0,
                        totalWithheld: 0
                    }
                );
            } else {
                setWHTRecords([]);
            }
        } catch (error) {
            toastify("Error fetching WHT records", "error");
            setWHTRecords([]);
        }
    };

    useEffect(() => {
        fetchWHTContracts();
        fetchWHTrecords();
    }, []);

    const activeContracts = whtContracts.filter(c => c.status === "Active").length;

    return (
        <div className="container-fluid px-4 py-3">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4 pb-2 border-bottom">
                <div>
                    <h4 className="mb-1">Withholding Tax Report</h4>
                    <p className="text-muted mb-0">Summary of WHT collections and contracts</p>
                </div>
            </div>

            {/* Loading */}
            {loading && (
                <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            )}

            {/* Stats Row */}
            {!loading && (
                <>
                    <div className="row mb-4">
                        <div className="col-md-4">
                            <div className="card">
                                <div className="card-body">
                                    <div className="d-flex justify-content-between align-items-center">
                                        <div>
                                            <h6 className="text-muted mb-2">WHT Contracts</h6>
                                            <h3 className="mb-0">{whtContracts.length}</h3>
                                            <small className="text-success">
                                                {activeContracts} active
                                            </small>
                                        </div>
                                        <i className="ti ti-file-invoice text-primary fs-1"></i>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="col-md-4">
                            <div 
                                className="card cursor-pointer"
                                style={{ cursor: "pointer" }}
                                onClick={() => setShowWHTModal(true)}
                            >
                                <div className="card-body">
                                    <div className="d-flex justify-content-between align-items-center">
                                        <div>
                                            <h6 className="text-muted mb-2">Total Withheld</h6>
                                            <h3 className="mb-0 text-warning">
                                                {formatCurrency(whtStats.totalWithheld || 0)}
                                            </h3>
                                            <small className="text-muted">
                                                {whtStats.totalRecords} records
                                            </small>
                                        </div>
                                        <i className="ti ti-building-bank text-warning fs-1"></i>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Recent Records Table */}
                    <div className="card mb-4">
                        <div className="card-header bg-white">
                            <h6 className="mb-0">Recent WHT Records</h6>
                        </div>
                        <div className="card-body p-0">
                            <div className="table-responsive">
                                <table className="table table-sm table-hover mb-0">
                                    <thead className="table-light">
                                        <tr>
                                            <th>Invoice #</th>
                                            <th>Customer</th>
                                            <th>Taxable Amount</th>
                                            <th>Rate</th>
                                            <th>Withheld Amount</th>
                                            <th>Date</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {whtRecords.length === 0 ? (
                                            <tr>
                                                <td colSpan="6" className="text-center py-4 text-muted">
                                                    No records found
                                                </td>
                                            </tr>
                                        ) : (
                                            whtRecords.slice(0, 5).map((record) => (
                                                <tr key={record._id}>
                                                    <td className="fw-medium">{record.invoiceNumber}</td>
                                                    <td>{record.customerName}</td>
                                                    <td>{formatCurrency(record.taxableAmount)}</td>
                                                    <td>
                                                        <span className="badge bg-secondary">{record.percentage}%</span>
                                                    </td>
                                                    <td className="text-warning fw-medium">
                                                        {formatCurrency(record.withheldAmount)}
                                                    </td>
                                                    <td className="text-muted small">
                                                        {new Date(record.createdAt).toLocaleDateString()}
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        {whtRecords.length > 5 && (
                            <div className="card-footer bg-white text-center">
                                <button 
                                    className="btn btn-link btn-sm p-0"
                                    onClick={() => setShowWHTModal(true)}
                                >
                                    View all {whtRecords.length} records →
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Active Contracts Table */}
                    <div className="card">
                        <div className="card-header bg-white">
                            <h6 className="mb-0">Active WHT Contracts</h6>
                        </div>
                        <div className="card-body p-0">
                            <div className="table-responsive">
                                <table className="table table-sm table-hover mb-0">
                                    <thead className="table-light">
                                        <tr>
                                            <th>Contract Name</th>
                                            <th>Customer</th>
                                            <th>Unit</th>
                                            <th>Frequency</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {whtContracts.filter(c => c.status === "Active").length === 0 ? (
                                            <tr>
                                                <td colSpan="6" className="text-center py-4 text-muted">
                                                    No active contracts
                                                </td>
                                            </tr>
                                        ) : (
                                            whtContracts
                                                .filter(c => c.status === "Active")
                                                .map((contract) => (
                                                    <tr key={contract._id}>
                                                        <td className="fw-medium">{contract.contractName}</td>
                                                        <td>{contract.customerName}</td>
                                                        <td>{contract.unitName}</td>
                                                        <td>
                                                            <span className="badge bg-primary">
                                                                {contract.paymentFrequency}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    {/* Modal for all records */}
                    {showWHTModal && (
                        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                            <div className="modal-dialog modal-xl">
                                <div className="modal-content">
                                    <div className="modal-header bg-light">
                                        <h6 className="modal-title">All Withholding Records</h6>
                                        <button 
                                            type="button" 
                                            className="btn-close" 
                                            onClick={() => setShowWHTModal(false)}
                                        ></button>
                                    </div>
                                    <div className="modal-body p-0">
                                        <div className="table-responsive">
                                            <table className="table table-sm mb-0">
                                                <thead className="table-light">
                                                    <tr>
                                                        <th>Invoice #</th>
                                                        <th>Customer</th>
                                                        <th>Account #</th>
                                                        <th>Taxable Amount</th>
                                                        <th>Rate</th>
                                                        <th>Withheld Amount</th>
                                                        <th>Date</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {whtRecords.map((record) => (
                                                        <tr key={record._id}>
                                                            <td className="fw-medium">{record.invoiceNumber}</td>
                                                            <td>{record.customerName}</td>
                                                            <td>{record.accountNumber}</td>
                                                            <td>{formatCurrency(record.taxableAmount)}</td>
                                                            <td>
                                                                <span className="badge bg-secondary">{record.percentage}%</span>
                                                            </td>
                                                            <td className="text-warning fw-medium">
                                                                {formatCurrency(record.withheldAmount)}
                                                            </td>
                                                            <td className="text-muted small">
                                                                {new Date(record.createdAt).toLocaleDateString()}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                    <div className="modal-footer">
                                        <button 
                                            type="button" 
                                            className="btn btn-sm btn-secondary" 
                                            onClick={() => setShowWHTModal(false)}
                                        >
                                            Close
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </>
            )}

            <style>{`
                .cursor-pointer:hover {
                    transform: translateY(-2px);
                    transition: transform 0.2s;
                }
                .fs-1 {
                    font-size: 2rem;
                }
            `}</style>
        </div>
    );
};

export default WHTReport;